/**
 * LibraryStatusCard — Shows library status in workspace sidebars
 * 
 * Displays:
 * - Library article count
 * - Recent saves (last 3)
 * - Edit preferences link
 * 
 * Used in Search and Learn sidebars to keep library front and center.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Separator } from '../ui/separator';
import { useWorkspace } from './WorkspaceShell';
import { libraryAPI } from '../../api';
import {
  BookOpen,
  FolderOpen,
  ArrowRight,
  Settings,
  Clock,
  Loader2,
} from 'lucide-react';

interface LibraryStatusCardProps {
  variant?: 'compact' | 'full';
  showRecentSaves?: boolean;
  maxRecentItems?: number;
}

interface RecentArticle {
  pmid: string;
  title: string;
  saved_at: string | null;
}

export const LibraryStatusCard: React.FC<LibraryStatusCardProps> = ({
  variant = 'compact',
  showRecentSaves = true,
  maxRecentItems = 3,
}) => {
  const navigate = useNavigate();
  const { bootstrap, loading: bootstrapLoading } = useWorkspace();
  
  const [recentArticles, setRecentArticles] = React.useState<RecentArticle[]>([]);
  const [loading, setLoading] = React.useState(false);

  // Load recent articles if we want to show them
  React.useEffect(() => {
    if (showRecentSaves && variant === 'full') {
      loadRecentArticles();
    }
  }, [showRecentSaves, variant]);

  const loadRecentArticles = async () => {
    try {
      setLoading(true);
      const response = await libraryAPI.getLibrary({ limit: maxRecentItems, sort_by: 'saved_at', sort_dir: 'desc' });
      const articles = (response.articles || [])
        .filter(a => a.pmid)
        .slice(0, maxRecentItems)
        .map(a => ({
          pmid: a.pmid!,
          title: a.title || 'Untitled',
          saved_at: a.saved_at || null,
        }));
      setRecentArticles(articles);
    } catch (error) {
      console.error('Failed to load recent articles:', error);
    } finally {
      setLoading(false);
    }
  };

  const libraryCount = bootstrap?.library_count || 0;
  const recentFromBootstrap = bootstrap?.recent_articles || [];

  // Format relative time
  const formatRelativeTime = (dateStr: string | null) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  if (variant === 'compact') {
    return (
      <div 
        className="p-3 rounded-lg bg-[hsl(var(--muted))] space-y-2"
        data-testid="library-status-card-compact"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-[hsl(var(--success))]/20 flex items-center justify-center">
              <FolderOpen className="w-4 h-4 text-[hsl(var(--success))]" />
            </div>
            <div>
              <div className="text-lg font-bold text-[hsl(var(--foreground))]">
                {bootstrapLoading ? '...' : libraryCount}
              </div>
              <div className="text-xs text-[hsl(var(--muted-foreground))]">
                in LitHub
              </div>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/library/shelf')}
            className="text-xs"
            data-testid="library-card-view-btn"
          >
            View
            <ArrowRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    );
  }

  // Full variant
  return (
    <div 
      className="space-y-3"
      data-testid="library-status-card-full"
    >
      {/* Library Count Card */}
      <div className="p-3 rounded-lg bg-[hsl(var(--muted))]">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[hsl(var(--success))]/20 flex items-center justify-center">
            <FolderOpen className="w-5 h-5 text-[hsl(var(--success))]" />
          </div>
          <div className="flex-1">
            <div className="text-2xl font-bold text-[hsl(var(--foreground))]">
              {bootstrapLoading ? '...' : libraryCount}
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              articles in your LitHub
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/library/shelf')}
            data-testid="library-card-open-btn"
          >
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Recent Saves */}
      {showRecentSaves && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium text-[hsl(var(--muted-foreground))] uppercase tracking-wide">
              Recently Saved
            </span>
          </div>
          
          {loading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="w-4 h-4 animate-spin text-[hsl(var(--muted-foreground))]" />
            </div>
          ) : (recentFromBootstrap.length > 0 || recentArticles.length > 0) ? (
            <div className="space-y-1">
              {(recentArticles.length > 0 ? recentArticles : recentFromBootstrap)
                .slice(0, maxRecentItems)
                .map((article, i) => (
                  <button
                    key={article.pmid || i}
                    onClick={() => navigate(`/library?article=${article.pmid}`)}
                    className="w-full text-left p-2 rounded-md hover:bg-[hsl(var(--muted))] transition-colors group"
                    data-testid={`recent-article-${i}`}
                  >
                    <div className="responsive-title text-sm text-[hsl(var(--foreground))] line-clamp-1 group-hover:text-[hsl(var(--primary))]">
                      {article.title}
                    </div>
                    {article.saved_at && (
                      <div className="flex items-center gap-1 text-xs text-[hsl(var(--muted-foreground))] mt-0.5">
                        <Clock className="w-3 h-3" />
                        {formatRelativeTime(article.saved_at)}
                      </div>
                    )}
                  </button>
                ))}
            </div>
          ) : (
            <div className="text-center py-4 text-sm text-[hsl(var(--muted-foreground))]">
              No saved articles yet.
              <Button
                variant="link"
                size="sm"
                onClick={() => navigate('/search')}
                className="text-[hsl(var(--primary))] px-1"
              >
                Search for articles
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Edit Preferences Link */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => navigate('/preferences')}
        className="w-full justify-start text-xs text-[hsl(var(--muted-foreground))]"
        data-testid="edit-preferences-link"
      >
        <Settings className="w-3.5 h-3.5 mr-2" />
        Edit digest preferences
      </Button>
    </div>
  );
};

export default LibraryStatusCard;
